// A mobile-photo-app style slider: a tick ruler that slides under a fixed centre needle. Drag (mouse / touch) or scroll sideways
// to change the value, ←/→ (Shift = ×10), Home / End, and a double-click resets to `neutral`. The caller shows the label + value.
import { useRef } from 'react';
import { cn } from '@/lib/utils';

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * @param value / onChange   the number
 * @param min / max          its range
 * @param neutral            the double-click reset value (and where the ruler's long tick sits)
 * @param pxPerUnit          ruler spacing — how far a drag moves the value
 * @param onDark             light ticks for a dark (photo overlay) background
 */
export default function RulerSlider({ value, onChange, min = -100, max = 100, neutral = 0, pxPerUnit = 3, ariaLabel, className, onDark = false }) {
    const dragRef = useRef(null);
    const set = (v) => {
        const next = clamp(Math.round(v), min, max);
        if (next !== value) onChange(next);
    };
    const onPointerDown = (e) => {
        dragRef.current = { x: e.clientX, v: value };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragRef.current) return;
        set(dragRef.current.v - (e.clientX - dragRef.current.x) / pxPerUnit);
    };
    const end = () => { dragRef.current = null; };
    const onWheel = (e) => {
        const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (!d) return;
        set(value + Math.sign(d) * Math.max(1, Math.round(Math.abs(d) / 12)));
    };
    const onKeyDown = (e) => {
        const step = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') set(value - step);
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') set(value + step);
        else if (e.key === 'Home') set(min);
        else if (e.key === 'End') set(max);
        else return;
        e.preventDefault();
    };

    const width = (max - min) * pxPerUnit;
    const offset = (value - min) * pxPerUnit; // how far the ruler has slid left of the needle
    const neutralAt = (neutral - min) * pxPerUnit;
    return (
        <div
            role="slider"
            tabIndex={0}
            aria-label={ariaLabel}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={end}
            onPointerCancel={end}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            onDoubleClick={() => set(neutral)}
            className={cn(
                'relative h-9 cursor-grab touch-none select-none overflow-hidden rounded-md outline-none active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-primary/40',
                '[mask-image:linear-gradient(90deg,transparent,#000_18%,#000_82%,transparent)]',
                className,
            )}
        >
            {/* The ruler: a short tick every 5 units, a tall one every 25, an extra-tall one at the neutral value */}
            <div
                className={cn('absolute inset-y-0', onDark ? 'text-white/50' : 'text-zinc-400 dark:text-zinc-500')}
                style={{
                    left: `calc(50% - ${offset}px)`,
                    width,
                    backgroundImage: `linear-gradient(currentColor, currentColor), repeating-linear-gradient(90deg, currentColor 0 1.5px, transparent 1.5px ${pxPerUnit * 25}px), repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px ${pxPerUnit * 5}px)`,
                    backgroundSize: `2px 70%, ${pxPerUnit * 25}px 55%, ${pxPerUnit * 5}px 30%`,
                    backgroundPosition: `${neutralAt - 1}px center, 0 center, 0 center`,
                    backgroundRepeat: 'no-repeat, repeat-x, repeat-x',
                }}
            />
            {/* The needle */}
            <div className={cn('pointer-events-none absolute left-1/2 top-1/2 h-[78%] w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary', !onDark && 'shadow-[0_0_0_2px_rgba(255,255,255,0.8)] dark:shadow-[0_0_0_2px_rgba(24,24,27,0.8)]')} />
        </div>
    );
}
